import { format, isBefore, isAfter, parseISO, parse, isEqual, isWeekend, add, startOfDay, sub, isWithinInterval, getDay } from 'date-fns';
import { mapGetters, mapActions } from 'vuex';

const durationRegexp = /P(?:(?<years>\d+)Y)?(?:(?<months>\d+)M)?(?:(?<weeks>\d+)W)?(?:(?<days>\d+)D)?(?:T(?:(?<hours>\d+)H)?(?:(?<minutes>\d+)M)?(?:(?<seconds>\d+)S)?)?/;
function parseDuration (str) {
  const match = durationRegexp.exec(str);
  if (match == null) {
    return null;
  }

  const result = match.groups;
  for (const key in result) {
    if (result[key] !== undefined) {
      result[key] = parseInt(result[key]);
    } else {
      result[key] = 0;
    }
  }

  return result;
}

function dateNow () {
  return Date.now();
}

class DateValidation {
  // eslint-disable-next-line space-before-function-paren
  constructor(joursFeries, heurePivot, delaisCommande, intervalleDateLivr) {
    this.joursFeries = joursFeries;
    this.heurePivot = heurePivot;
    this.delaisCommande = delaisCommande;
    this.intervalleDateLivr = intervalleDateLivr;
  }

  _validationDate = function (date, nbJoursMinLivraisons, joursLivraison) {
    if (typeof date === 'string') {
      date = parseISO(date);
    }

    // calcul en fonction de l'heure pivot, sauf si contrainte de livraison plus restrictive
    const nbJoursMiniAvantLivraison = Math.max(
      isBefore(dateNow(), this.heurePivot) && this.isJourOuvre(dateNow()) ? 1 : 2,
      nbJoursMinLivraisons || 0
    );
    let dateMini = startOfDay(this.addBusinessDays(dateNow(), nbJoursMiniAvantLivraison));

    if (isBefore(date, dateMini)) {
      return 'La date doit être supérieure ou égale à ' + format(dateMini, 'dd/MM/yyyy');
    }

    if (this.isJourOuvre(date) === false) {
      return 'La date doit être un jour ouvré';
    }
    if (this.validationDelais(date) === 'ERROR') {
      return 'Le délais maximum est dépassé';
    }

    const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const day = days[getDay(date)];

    if (joursLivraison && joursLivraison[day] === false) {
      const joursPossibles = days.filter(day => joursLivraison[day] === true);
      return 'Jours autorisés : ' + joursPossibles.length ? joursPossibles.join(', ') : 'aucun';
    }

    return null;
  };

  _validationDateFin = function (dateDebut, dateFin, nbJoursEcartMin, nbJoursMinLivraisons, joursLivraison) {
    if (typeof dateDebut === 'string') {
      dateDebut = parseISO(dateDebut);
    }
    if (typeof dateFin === 'string') {
      dateFin = parseISO(dateFin);
    }

    const err = this._validationDate(dateFin, nbJoursMinLivraisons, joursLivraison);
    if (err) {
      return err;
    }

    if (dateDebut) {
      if (nbJoursEcartMin === 0) {
        if (isBefore(dateFin, dateDebut)) {
          return 'La date au plus tard doit être supérieure ou égale à la date au plus tôt';
        }
      } else if (nbJoursEcartMin === 1) {
        if (isBefore(dateFin, this.addBusinessDays(dateDebut, 1))) {
          return 'La date au plus tard doit être supérieure à la date au plus tôt';
        }
      } else {
        if (isBefore(dateFin, this.addBusinessDays(dateDebut, nbJoursEcartMin))) {
          return 'La date au plus tard doit être postérieure de ' + nbJoursEcartMin + ' jours minimum à la date au plus tôt';
        }
      }
    }

    return null;
  }

  validationDelais = function (date) {
    if (typeof date === 'string') {
      date = parseISO(date);
    }

    const delaisErreur = parseDuration(this.delaisCommande.split('/')[1]);
    const dateErreur = add(startOfDay(dateNow()), delaisErreur);

    if (isAfter(date, dateErreur)) { return 'ERROR'; }

    const delaisAlerte = parseDuration(this.delaisCommande.split('/')[0]);
    const dateAlerte = this.addDurationBuisnessDays(startOfDay(dateNow()), delaisAlerte);

    if (isAfter(date, dateAlerte)) { return 'WARN'; }

    return false;
  }

  validationIntervalle = function (dateDebut, dateFin) {
    if (typeof dateDebut === 'string') {
      dateDebut = parseISO(dateDebut);
    }

    if (typeof dateFin === 'string') {
      dateFin = parseISO(dateFin);
    }
    const durationInterval = parseDuration(this.intervalleDateLivr);
    if (durationInterval) {
      const dateMaxinterval = this.addDurationBuisnessDays(startOfDay(dateDebut), durationInterval);

      if (isAfter(dateFin, dateMaxinterval)) { return 'WARN'; }
    }
    return false;
  }

  addDurationBuisnessDays = function (date, duration) {
    const _duration = Object.assign({}, duration, { days: 0 });
    date = add(date, _duration);
    date = this.addBusinessDays(date, duration.days);
    return date;
  }

  addBusinessDays = function (date, nb) {
    const _add = nb >= 0;
    nb = Math.abs(nb);
    if (typeof date === 'string') {
      date = parseISO(date);
    }
    let nbAdded = 0;
    while (nbAdded < nb) {
      date = add(date, { days: _add ? 1 : -1 });
      if (this.isJourOuvre(date)) {
        nbAdded += 1;
      }
    }

    return date;
  };

  differenceInBusinessDays (date1, date2) {
    if (typeof date1 === 'string') {
      date1 = parseISO(date1);
    }
    if (typeof date2 === 'string') {
      date2 = parseISO(date2);
    }

    let neg = false;

    if (isBefore(date2, date1)) {
      [date1, date2] = [date2, date1];
      neg = true;
    }

    let diff = 0;
    while (isBefore(date1, date2)) {
      date1 = this.addBusinessDays(date1, 1);
      diff += 1;
    }

    return neg ? -diff : diff;
  }

  isJourOuvre = function (date) {
    if (typeof date === 'string') {
      date = parseISO(date);
    }
    date = startOfDay(date);
    if (isWeekend(date)) {
      return false;
    } else {
      for (const jour of this.joursFeries) {
        if (isEqual(date, parseISO(jour))) {
          return false;
        }
      }
    }
    return true;
  }

  validationDates = function (dateDebut, dateFin, nbJoursEcartMin, nbJoursMinLivraisons, joursLivraison, delaiCmdAnticipee) {
    return {
      'DATE_DEBUT': this._validationDate(dateDebut, nbJoursMinLivraisons, joursLivraison, delaiCmdAnticipee),
      'DATE_FIN': this._validationDateFin(dateDebut, dateFin, nbJoursEcartMin, nbJoursMinLivraisons, joursLivraison)
    };
  }

  validationDate = function (key, dateDebut, dateFin, nbJoursEcartMin, nbJoursMinLivraisons, joursLivraison, delaiCmdAnticipee) {
    return this.validationDates(dateDebut, dateFin, nbJoursEcartMin, nbJoursMinLivraisons, joursLivraison, delaiCmdAnticipee)[key];
  }

  firstDateAllowedBefore = function (date, nbJoursMinLivraisons, joursLivraison) {
    if (date === undefined) {
      date = dateNow();
    }
    if (typeof date === 'string') {
      date = parseISO(date);
    }

    let d = this.addBusinessDays(date, -1);

    while (this._validationDate(d, nbJoursMinLivraisons, joursLivraison) !== null) {
      d = this.addBusinessDays(d, 1);
      if (isBefore(d, dateNow())) {
        return null;
      }
    }

    return d;
  }

  calculDateDefaut (key, dateDebut, dateFin, nbJoursEcartMin, nbJoursMinLivraisons, joursLivraison) {
    if (typeof dateDebut === 'string') {
      dateDebut = parseISO(dateDebut);
    }
    if (typeof dateFin === 'string') {
      dateFin = parseISO(dateFin);
    }

    if (key === 'DATE_FIN') {
      if (!dateDebut) {
        dateDebut = this.calculDateDefaut('DATE_DEBUT', null, null, null, nbJoursMinLivraisons, joursLivraison);
      }
      let date = dateDebut;

      let i = 0;
      while (this._validationDateFin(dateDebut, date, nbJoursEcartMin, nbJoursMinLivraisons, joursLivraison) !== null) {
        date = this.addBusinessDays(date, 1);
        if (i++ > 30) {
          return dateDebut;
        }
      }

      return date;
    }

    if (key === 'DATE_DEBUT') {
      if (!dateFin) {
        let date = dateNow();
        while (this._validationDate(date) !== null) {
          date = this.addBusinessDays(date, 1);
        }

        return date;
      } else {
        let date = dateFin;

        // on cherche la date la plus proche de la date de fin respectant les contraintes
        while (!isEqual(this.validationDates(date, dateFin, nbJoursEcartMin, nbJoursMinLivraisons, joursLivraison), { 'DATE_DEBUT': null, 'DATE_FIN': null })) {
          date = this.addBusinessDays(date, -1);
          if (isBefore(date, dateNow())) {
            date = null;
            break;
          }
        }

        // sinon, on cherche la premiere date de debut valide, la date de fin sera KO
        if (!date) {
          date = this.calculDateDefaut('DATE_DEBUT', null, null, 0, nbJoursMinLivraisons, joursLivraison);
        }

        return date;
      }
    }

    return null;
  }

  firstDateAllowedAfter = function (date, nbJoursMinLivraisons, joursLivraison) {
    if (date === undefined) {
      date = dateNow();
    }
    if (typeof date === 'string') {
      date = parseISO(date);
    }

    let d = this.addBusinessDays(date, 1);

    while (this._validationDate(d, nbJoursMinLivraisons, joursLivraison) !== null) {
      d = this.addBusinessDays(d, 1);
    }

    return d;
  }
}

const mixin = {
  data: () => ({
    $dateValidation: null
  }),
  computed: {
    ...mapGetters({ paramsCommande: 'referentiel/paramsCommande', sessionInfos: 'user/sessionInfos' })

  },
  methods: {
    ...mapActions({ loadParamsCommande: 'referentiel/loadParamsCommande' }),
    async $dateValidationLoad () {
      if (!this.paramsCommande) {
        await this.loadParamsCommande({});
      }

      const [heurePivotStr, delaisStr] = this.paramsCommande.heurePivot.split('/');

      let heurePivot = parse(heurePivotStr, 'HH:mm:ss', dateNow());
      const delaiHeurePivot = parseDuration(delaisStr);
      const connectionTime = this.sessionInfos.connectionTime;

      if (isWithinInterval(connectionTime, { start: sub(heurePivot, delaiHeurePivot), end: heurePivot })) {
        heurePivot = add(connectionTime, delaiHeurePivot);
      }

      this.$dateValidation = new DateValidation(this.paramsCommande.joursFeries, heurePivot, this.paramsCommande.delaisCommande, this.paramsCommande.intervalleLivraison);
    }
  }
};

export default mixin;
